import React from 'react';
import sectionTypeCheckers from '../sectionTypeCheckers';
import useHasBonusSubPeriods from '../../hooks/useHasBonusSubPeriods';
import Performance from '../Sections/Performance';
import Metric from '../Sections/Metric';
import Grid from '../Sections/Grid';
import Properties from '../Sections/Properties';
import Comments from 'components/Comments';
import useCommentsWidgetProps from 'components/PerformanceBoards/hooks/useCommentsWidgetProps';
import TargetProgress from '../Sections/TargetProgress';
import RelatedDocuments from '../Sections/RelatedDocuments';
import RelatedMetricDocuments from '../Sections/RelatedMetricDocuments';
import AllMetricsGate from 'components/AllMetricsGate';
import BonusTotalReward from '../Sections/BonusTotalReward';
import BonusSubPeriodFactTable from '../Sections/BonusSubPeriodFactTable';
import BonusRewardBody from '../../../DriverAX/components/BonusReward/BonusRewardBody';
import BonusEligibilitySlideoutSection from '../../../DriverAX/components/BonusEligibility/BonusEligibilitySlideoutSection';
import RecognitionPayments from '../Sections/RecognitionPayments';

const DisplaySection = ({
  section,
}: {
  section: PerformanceBoardTypes.SlideOut.SlideOutSection;
}) => {
  const {
    commentableId,
    commentableType,
    emailLink,
    mentionedEmailBody,
    watcherEmailBody,
  } = useCommentsWidgetProps();
  // Sub-period programmes show only the per-week fact table for the bonus
  // sections (no single-week card); sign-off boards add a summed total header.
  const hasSubPeriods = useHasBonusSubPeriods();
  if (sectionTypeCheckers.isPerformanceSection(section)) {
    return <Performance section={section} />;
  }
  if (sectionTypeCheckers.isTargetProgressSection(section)) {
    return <TargetProgress section={section} />;
  }
  if (sectionTypeCheckers.isMetricSection(section)) {
    return <Metric section={section} />;
  }
  if (sectionTypeCheckers.isGridSection(section)) {
    return <Grid section={section} />;
  }
  if (sectionTypeCheckers.isPropertiesSection(section)) {
    return <Properties section={section} />;
  }
  if (sectionTypeCheckers.isRelatedDocumentsSection(section)) {
    return <RelatedDocuments section={section} />;
  }
  if (sectionTypeCheckers.isRelatedMetricDocumentsSection(section)) {
    // The bonus metric's definition (and, for compounds, its constituents) may
    // not be in the on-demand metric cache, so ensure all metrics are loaded
    // before resolving the metric's datasets/filters.
    return (
      <AllMetricsGate>
        <RelatedMetricDocuments section={section} />
      </AllMetricsGate>
    );
  }
  if (sectionTypeCheckers.isBonusRewardSection(section)) {
    // Sub-period programme: the reward name + period total live in the section
    // header (BonusSubPeriodRewardHeading), so here we render only the weekly
    // fact table. Ordinary programme: the single-week card.
    if (hasSubPeriods) {
      return (
        <BonusSubPeriodFactTable
          bonusConfigId={section.bonusConfigId}
          fields={section.factTableFields}
          renderMode={section.renderMode}
        />
      );
    }
    return <BonusRewardBody rewardName={section.rewardName} />;
  }
  if (sectionTypeCheckers.isBonusTotalRewardSection(section)) {
    if (hasSubPeriods) {
      return (
        <BonusSubPeriodFactTable
          bonusConfigId={section.bonusConfigId}
          fields={section.factTableFields}
          title={'Total'}
          showTotal
          totalFieldOverride={section.totalValueField}
          renderMode={section.renderMode}
        />
      );
    }
    return <BonusTotalReward />;
  }
  if (sectionTypeCheckers.isBonusEligibilitySection(section)) {
    // Sub-period programmes replace the eligibility card with the weekly fact
    // table (same as the reward sections) so eligibility overrides work on any
    // sub-period board, not just sign-off boards. Only when fact-table fields are
    // configured, though — the table renders nothing without them, so sections
    // saved before fact tables existed keep the card.
    if (hasSubPeriods && !!section.factTableFields?.length) {
      return (
        <BonusSubPeriodFactTable
          bonusConfigId={section.bonusConfigId}
          fields={section.factTableFields}
          renderMode={section.renderMode}
        />
      );
    }
    return <BonusEligibilitySlideoutSection />;
  }
  if (sectionTypeCheckers.isRecognitionPaymentsSection(section)) {
    return <RecognitionPayments />;
  }
  if (
    sectionTypeCheckers.isCommentSection(section) &&
    commentableId &&
    commentableType &&
    emailLink
  ) {
    return (
      <Comments
        commentableId={commentableId}
        commentableType={commentableType}
        mentionedEmailBody={mentionedEmailBody}
        watcherEmailBody={watcherEmailBody}
        emailLink={emailLink}
      />
    );
  }

  return null;
};

export default DisplaySection;
